import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectCreative, Navigation, Pagination, FreeMode, Controller } from "swiper/modules";
import "swiper/css/effect-creative";
import Marquee from "react-fast-marquee";
import { FaChevronLeft, FaHeart, FaShippingFast, FaMedal, FaHeadset, FaShoppingBag, FaArrowRight } from "react-icons/fa";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/effect-coverflow";
import "swiper/css/pagination";
import "./Home.css";

import { useEffect, useState, useMemo, useCallback, Suspense } from "react";
import { Link, useNavigate } from "react-router";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useDarkMode } from "../../App";
import { fetchBanners } from "../../redux/actions/BannersAction";
import { useDispatch, useSelector } from "react-redux";
import { fetchProductionCompany } from "../../redux/actions/ProductionCompanyAction";
import { fetchAllProducts } from "../../redux/actions/AllProductsAction";
import { fetchProductNewest } from "../../redux/actions/ProductsNewestAction";
import { fetchCategories } from "../../redux/actions/CategoriesAction";
import { fetchProductOffers } from "../../redux/actions/ProductsOfferActions";
import { fetchAboutUs } from "../../redux/actions/aboutUsAction";
import { fetchServiceItems } from "../../redux/actions/serviceItemsAction";
import { toast } from "react-toastify";
import { addToCart, fetchCartItems } from "../../redux/actions/cartAction";
import { toggleFavorite, fetchFavorites } from "../../redux/actions/favoriteAction";



const sectionVariants = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};
const cardVariants = {
    hidden: { opacity: 0, x: 60 },
    visible: (i) => ({
        opacity: 1,
        x: 0,
        transition: {
            delay: i * 0.12,
            duration: 0.6,
            type: "spring",
            stiffness: 60,
        },
    }),
};

function Home() {
    const { isDarkMode } = useDarkMode();
    
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
    return (
        <motion.section
            className={`home ${isDarkMode ? 'dark-mode' : ''}`}
            style={{ background: isDarkMode ? '#0a0e27' : 'transparent' }}
            initial="hidden"
            animate="visible"
            variants={sectionVariants}
        >
            <Suspense fallback={<div className="text-center py-5"><div className="spinner-border text-primary"></div></div>}>
                <MainSlider />
                <AboutUsSection />
                {/* <FeaturedProducts /> */}
                <HomeCategory />
                <AllProducts />
                <BestMarka />
                <Collection />
            </Suspense>
        </motion.section>
    );
}

function AboutUsSection() {
    const { t, i18n } = useTranslation("global");
    const { isDarkMode } = useDarkMode();
    const dispatch = useDispatch();
    const { data: aboutUsData, isLoading, error } = useSelector((state) => state.aboutUs);
    const { data: serviceItems, isLoading: serviceItemsLoading, error: serviceItemsError } = useSelector((state) => state.serviceItems);

    // Memoized error suppression
    const suppressConsoleErrors = useCallback(() => {
        const originalError = console.error;
        const originalWarn = console.warn;
        
        console.error = (...args) => {
            const errorMessage = args[0]?.toString() || '';
            if (errorMessage.includes('CORS policy') || 
                errorMessage.includes('Access-Control-Allow-Origin') ||
                errorMessage.includes('NetworkError') ||
                errorMessage.includes('ERR_FAILED') ||
                errorMessage.includes('googleads.g.doubleclick.net') ||
                errorMessage.includes('fonts.gstatic.com') ||
                errorMessage.includes('youtube.com') ||
                errorMessage.includes('googlevideo.com')) {
                return;
            }
            originalError.apply(console, args);
        };
        
        console.warn = (...args) => {
            const warnMessage = args[0]?.toString() || '';
            if (warnMessage.includes('Permissions policy violation') ||
                warnMessage.includes('picture-in-picture') ||
                warnMessage.includes('youtube.com')) {
                return;
            }
            originalWarn.apply(console, args);
        };
        
        return () => {
            console.error = originalError;
            console.warn = originalWarn;
        };
    }, []);

    useEffect(() => {
        const cleanup = suppressConsoleErrors();
        return cleanup;
    }, [suppressConsoleErrors]);


    useEffect(() => {
        dispatch(fetchAboutUs(i18n.language));
        dispatch(fetchServiceItems(i18n.language));
    }, [dispatch, i18n.language]);

    if (isLoading) {
        return (
            <section className="about-us-section py-5" style={{ 
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                position: 'relative',
                overflow: 'hidden'
            }}>
                <div className="container">
                    <div className="text-center">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    if (error) {
        return (
            <section className="about-us-section py-5" style={{ 
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                position: 'relative',
                overflow: 'hidden'
            }}>
                <div className="container">
                    <div className="text-center">
                        <p className="text-danger">خطأ في تحميل بيانات من نحن</p>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className={`about-us-section py-5 ${isDarkMode ? 'dark-mode' : ''}`} style={{ 
            background: isDarkMode ? '#0a0e27' : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
            position: 'relative',
            overflow: 'hidden'
        }}>
            <div className="container">
                <div className="row align-items-center">
                    <div className="col-lg-6">
                        <motion.div
                            initial={{ opacity: 0, x: i18n.language === 'ar' ? 50 : -50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                            className="about-content"
                        >
                            <h2 className="about-title">
                                {aboutUsData?.title || t('home.aboutUs.title')}
                            </h2>
                            <p className="about-description">
                                {aboutUsData?.description || t('home.aboutUs.description')}
                            </p>
                            
                            <div className="features-grid">
                                {(serviceItems && serviceItems.length > 0 ? serviceItems : [
                                    { title: t('home.aboutUs.features.freeShipping'), description: t('home.sidebar.freeShippingDesc'), icon: "FaShippingFast" },
                                    { title: t('home.aboutUs.features.originalProducts'), description: t('home.sidebar.originalProductsDesc'), icon: "FaMedal" },
                                    { title: t('home.aboutUs.features.support24'), description: t('home.sidebar.support24Desc'), icon: "FaHeadset" }
                                ]).map((service, index) => (
                                    <motion.div 
                                        key={index}
                                        className="mythic-feature"
                                        initial={{ opacity: 0, x: i18n.language === 'ar' ? 30 : -30 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.5, delay: index * 0.1 }}
                                    >
                                        <div className="mythic-icon-box">
                                            {service.images && service.images.length > 0 ? (
                                                <img 
                                                    src={service.images[0]} 
                                                    alt={service.title}
                                                    style={{ width: '25px', height: '25px', objectFit: 'contain' }}
                                                />
                                            ) : <FaHeart />}
                                        </div>
                                        <div className="mythic-feature-text">
                                            <h4>{service.title}</h4>
                                            <p>{service.description}</p>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                    <div className="col-lg-6">
                        <motion.div
                            initial={{ opacity: 0, x: i18n.language === 'ar' ? -50 : 50 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 1 }}
                            className="mythic-video-container"
                        >
                            <div className="video-glow"></div>
                            <div className="mythic-video-inner">
                                <iframe
                                    src={aboutUsData?.video_url ? aboutUsData.video_url.replace('youtu.be/', 'youtube.com/embed/') : "https://www.youtube.com/embed/dQw4w9WgXcQ?rel=0&modestbranding=1"}
                                    title={aboutUsData?.title || "كيفية استخدام موقع One Plus"}
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        border: 'none'
                                    }}
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                ></iframe>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
}


function MainSlider() {
    const { t, i18n } = useTranslation("global");
    const { isDarkMode } = useDarkMode();
    const dispatch = useDispatch();
    const { banners, isLoading, error } = useSelector((state) => state.banners);
    const { data: serviceItems } = useSelector((state) => state.serviceItems);
    const swiperKey = useMemo(() => `swiper-${i18n.language}-${isDarkMode}`, [i18n.language, isDarkMode]);
    const navigate = useNavigate();
    
    const handleShopNow = useCallback(() => {
        navigate('/categories');
    }, [navigate]);

    useEffect(() => {
        dispatch(fetchBanners(i18n.language));
        dispatch(fetchServiceItems(i18n.language));
    }, [dispatch, i18n.language]);

    if (isLoading) {
        return (
            <div className="main-slider-skeleton" style={{ height: '500px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="spinner-border text-primary" role="status"></div>
            </div>
        );
    }

    if (error) {
        return null; // Silently fail or minimal error UI
    }

    return (
        <section className={`hero-super-section ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className="container">
                <div className="row g-4 h-100">
                    {/* Main Hero Slider - 8 Cols */}
                    <div className="col-lg-8">
                        <div className="hero-slider-wrapper">
                            <Swiper
                                key={swiperKey}
                                grabCursor={true}
                                effect={'creative'}
                                creativeEffect={{
                                    prev: {
                                        shadow: true,
                                        translate: [0, 0, -400],
                                    },
                                    next: {
                                        translate: ['100%', 0, 0],
                                    },
                                }}
                                speed={1000}
                                autoplay={{
                                    delay: 6000,
                                    disableOnInteraction: false,
                                }}
                                pagination={{
                                    clickable: true,
                                    dynamicBullets: true,
                                }}
                                navigation={true}
                                modules={[Autoplay, Pagination, Navigation, EffectCreative]}
                                className="heroSwiper"
                            >
                                {banners && banners.length > 0 ? banners.map((banner, index) => (
                                    <SwiperSlide key={banner.id || index}>
                                        <div className="hero-slide-item">
                                            <div className="hero-image-container">
                                                <img
                                                    src={banner.image || '/Frame.png'}
                                                    alt={banner.title || 'Welcome'}
                                                />
                                                <div className="hero-overlay-gradient"></div>
                                            </div>
                                            <div className="hero-content">
                                                <motion.h2 
                                                    initial={{ opacity: 0, y: 30 }}
                                                    whileInView={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: 0.2, duration: 0.8 }}
                                                    className="hero-title"
                                                >
                                                    {banner.title}
                                                </motion.h2>
                                                <motion.p 
                                                    initial={{ opacity: 0, y: 30 }}
                                                    whileInView={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: 0.4, duration: 0.8 }}
                                                    className="hero-desc"
                                                >
                                                    {banner.description}
                                                </motion.p>
                                                <motion.button 
                                                    initial={{ opacity: 0, scale: 0.9 }}
                                                    whileInView={{ opacity: 1, scale: 1 }}
                                                    whileHover={{ scale: 1.05 }}
                                                    whileTap={{ scale: 0.95 }}
                                                    transition={{ delay: 0.6 }}
                                                    onClick={handleShopNow} 
                                                    className="hero-btn-premium"
                                                >
                                                    {t('home.mainSlider.shopNow')} <FaArrowRight />
                                                </motion.button>
                                            </div>
                                        </div>
                                    </SwiperSlide>
                                )) : (
                                    <SwiperSlide>
                                        <div className="hero-slide-item">
                                            <div className="hero-image-container">
                                                <img src="/Frame.png" alt="Default" />
                                                <div className="hero-overlay-gradient"></div>
                                            </div>
                                            <div className="hero-content">
                                                <h2 className="hero-title">{t('home.mainSlider.defaultTitle')}</h2>
                                                <p className="hero-desc">{t('home.mainSlider.defaultDescription')}</p>
                                                <button onClick={handleShopNow} className="hero-btn-premium">
                                                    {t('home.mainSlider.shopNow')} <FaArrowRight />
                                                </button>
                                            </div>
                                        </div>
                                    </SwiperSlide>
                                )}
                            </Swiper>
                        </div>
                    </div>

                    {/* Side Banners / Services - 4 Cols */}
                    <div className="col-lg-4 d-none d-lg-block">
                        <div className="side-marketing-grid h-100">
                             {/* Top Side Card */}
                             <motion.div 
                                className="side-marketing-card glass-card top-card"
                                initial={{ opacity: 0, x: 50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.2 }}
                             >
                                <div className="side-card-content">
                                    <div className="icon-wrapper red-glow">
                                        <FaMedal />
                                    </div>
                                    <div>
                                        <h5>{t('home.sidebar.genuine', '100% Genuine')}</h5>
                                        <p>{t('home.sidebar.genuineDesc', 'All products are original')}</p>
                                    </div>
                                </div>
                             </motion.div>

                             {/* Middle Side Card */}
                             <motion.div 
                                className="side-marketing-card glass-card mid-card"
                                initial={{ opacity: 0, x: 50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.4 }}
                             >
                                <div className="side-card-content">
                                    <div className="icon-wrapper blue-glow">
                                        <FaShippingFast />
                                    </div>
                                    <div>
                                        <h5>{t('home.sidebar.fastDelivery', 'Fast Delivery')}</h5>
                                        <p>{t('home.sidebar.fastDeliveryDesc', 'Safe & Fast Arrival')}</p>
                                    </div>
                                </div>
                             </motion.div>

                             {/* Bottom Side Card (or Swiper if many services) */}
                             <motion.div 
                                className="side-marketing-card glass-card bottom-card"
                                initial={{ opacity: 0, x: 50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.6 }}
                             >
                                <div className="side-card-content">
                                    <div className="icon-wrapper green-glow">
                                        <FaHeadset />
                                    </div>
                                    <div>
                                        <h5>{t('home.sidebar.support', '24/7 Support')}</h5>
                                        <p>{t('home.sidebar.supportDesc', 'Always here for you')}</p>
                                    </div>
                                </div>
                             </motion.div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}



function Collection() {
    const { t, i18n } = useTranslation("global");
    const { isDarkMode } = useDarkMode();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    // Memoized navigation handler
    const handleProductClick = useCallback((productId) => {
        navigate(`/productDetails/${productId}`);
    }, [navigate]);

    const {
        offers: products = [],
        pagination: offersPagination = {},
        isLoading,
    } = useSelector((state) => state.offers);

    const { items: favoriteItems = [] } = useSelector((state) => state.favorite) || {};
    const userTools = JSON.parse(localStorage.getItem("userTools"));
    const token = localStorage.getItem("token") || userTools?.token;
    const isSocial = token === 'social_login_placeholder';

    useEffect(() => {
        dispatch(fetchProductOffers({ lang: i18n.language, page: 1 }));
        if (token && !isSocial) {
            dispatch(fetchFavorites(i18n.language));
        }
    }, [dispatch, i18n.language, token, isSocial]);

    const handleToggleFavorite = useCallback((e, productId) => {
        e.stopPropagation();
        if (!token || isSocial) {
            toast.info(t('home.loginRequired'));
            return;
        }
        dispatch(toggleFavorite({ productId, language: i18n.language }))
            .unwrap()
            .then((res) => {
                toast.success(res.message || t('favorites.updated'));
                setTimeout(() => {
                    dispatch(fetchFavorites(i18n.language));
                }, 100);
            })
            .catch((err) => toast(err || t('favorites.error')));
    }, [dispatch, token, i18n.language, t]);

    const isFavorite = useCallback((productId) => {
        return favoriteItems?.some(item => item.id === productId);
    }, [favoriteItems]);

    const handlePageChange = useCallback((page) => {
        dispatch(fetchProductOffers({ lang: i18n.language, page }));
    }, [dispatch, i18n.language]);

    const [productQuantities, setProductQuantities] = useState({});

    const handleUpdateQuantity = (product, delta) => {
        const step = product.count || 1;
        setProductQuantities(prev => {
            const currentQty = prev[product.id] || step;
            const newQty = Math.max(step, currentQty + (delta * step));
            return { ...prev, [product.id]: newQty };
        });
    };

    return (
        <motion.section
            className={`offers-section py-5 ${isDarkMode ? 'dark-mode' : ''}`}
            style={{ background: isDarkMode ? '#0a0e27' : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={sectionVariants}
        >
            <div className="container">
                <div className="row">
                    {/* Section Header */}
                    <div className="col-12 text-center mb-5">
                        <motion.h2
                            className="fw-bold mb-3"
                            style={{ color: isDarkMode ? 'var(--color-text)' : '#000c3d', fontSize: '2.5rem' }}
                            initial={{ opacity: 0, y: -30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7 }}
                        >
                            {t("navbar.offers")}
                        </motion.h2>
                        <motion.p
                            className="text-muted"
                            style={{ fontSize: '1.2rem', color: isDarkMode ? 'var(--color-muted)' : undefined }}
                            initial={{ opacity: 0, y: -20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.2 }}
                        >
                            {t('home.offers.subtitle')}
                        </motion.p>
                    </div>

                    <div className="col-12">
                        {isLoading ? (
                            <div className="text-center my-5">
                                <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                                    <span className="visually-hidden">{t('home.loading')}</span>
                                </div>
                                <p className="mt-3 text-muted">{t('home.loading')}</p>
                            </div>
                        ) : products.length > 0 ? (
                            <div className="row g-4">
                                {Array.isArray(products) && products?.slice(0, 8).map((product, index) => (
                                    <div className="col-12 col-sm-6 col-lg-3" key={product.id}>
                                        <motion.div
                                            className={`mythic-product-card ${isDarkMode ? 'dark-mode' : ''}`}
                                            variants={cardVariants}
                                            custom={index}
                                            initial="hidden"
                                            whileInView="visible"
                                            viewport={{ once: true }}
                                            onClick={() => handleProductClick(product.id)}
                                        >
                                            {/* Header Actions */}
                                            <div className="card-actions-top">
                                                <button 
                                                    className={`action-btn fav-btn ${isFavorite(product.id) ? 'active' : ''}`}
                                                    onClick={(e) => handleToggleFavorite(e, product.id)}
                                                >
                                                    <FaHeart />
                                                </button>
                                                {product.offer_percent > 0 && (
                                                    <span className="discount-badge">-{product.offer_percent}%</span>
                                                )}
                                                {product.offer_type === 'fixed' && product.offer > 0 && (
                                                    <span className="discount-badge">-{product.offer} جنيه</span>
                                                )}
                                            </div>

                                            {/* Product Media - Keeping Swiper if multiple images exist */}
                                            <div className="product-media-wrap">
                                                {product.images && product.images.length > 1 ? (
                                                    <Swiper
                                                        modules={[Autoplay, Pagination]}
                                                        spaceBetween={0}
                                                        slidesPerView={1}
                                                        pagination={{ 
                                                            clickable: true,
                                                            bulletClass: 'swiper-pagination-bullet-custom',
                                                            bulletActiveClass: 'swiper-pagination-bullet-active-custom'
                                                        }}
                                                        autoplay={{ 
                                                            delay: 3000, 
                                                            disableOnInteraction: false,
                                                            pauseOnMouseEnter: true
                                                        }}
                                                        style={{ height: '100%' }}
                                                    >
                                                        {product.images.map((img, idx) => (
                                                            <SwiperSlide key={idx}>
                                                                <img 
                                                                    src={img} 
                                                                    alt={product.name}
                                                                    className="product-main-img"
                                                                    onError={(e) => { e.target.src = "/Frame.png"; }}
                                                                />
                                                            </SwiperSlide>
                                                        ))}
                                                    </Swiper>
                                                ) : (
                                                    <img
                                                        src={product.images?.[0] || "/Frame.png"}
                                                        alt={product.name}
                                                        className="product-main-img"
                                                        onError={(e) => { e.target.src = "/Frame.png"; }}
                                                    />
                                                )}
                                                <div className="media-overlay">
                                                    <span className="quick-view-tag">{t('home.allProducts.viewDetails')}</span>
                                                </div>
                                            </div>

                                            {/* Product Body */}
                                            <div className="product-body-content">
                                                <div className="category-tag">{product.category}</div>
                                                <h4 className="product-title-text">{product.name}</h4>
                                                {product.code && (
                                                    <div className="product-code small text-muted mb-1" style={{ fontSize: '0.85rem' }}>
                                                        {t('product.code')}: {product.code}
                                                    </div>
                                                )}
                                                
                                                <div className="price-section-new">
                                                    {product.final_price < product.price ? (
                                                        <>
                                                            <span className="current-price">{product.final_price} <small>{t("cart.currency")}</small></span>
                                                            <span className="old-price">{product.price} <small>{t("cart.currency")}</small></span>
                                                        </>
                                                    ) : (
                                                        <span className="current-price">{product.price} <small>{t("cart.currency")}</small></span>
                                                    )}
                                                </div>

                                                {/* Add to Cart Control */}
                                                <div className="cart-control-wrap" onClick={(e) => e.stopPropagation()}>
                                                    <div className="qty-selector">
                                                        <button onClick={() => handleUpdateQuantity(product, -1)}>−</button>
                                                        <span>{productQuantities[product.id] || product.count || 1}</span>
                                                        <button onClick={() => handleUpdateQuantity(product, 1)}>+</button>
                                                    </div>
                                                    <button 
                                                        className="mythic-add-btn"
                                                        onClick={() => {
                                                            const qty = productQuantities[product.id] || product.count || 1;
                                                            dispatch(addToCart({ 
                                                                product_id: product.id, 
                                                                qty: qty, 
                                                                language: i18n.language 
                                                            })).unwrap()
                                                            .then(() => {
                                                                toast.success(t('cart.addedSuccessfully'));
                                                                dispatch(fetchCartItems(i18n.language));
                                                            })
                                                            .catch((err) => toast(err.message || t('cart.addError')));
                                                        }}
                                                    >
                                                        <i className="fas fa-shopping-basket"></i>
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="card-hover-aura"></div>
                                        </motion.div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center my-5">
                                <div className="alert alert-info" role="alert" style={{ fontSize: '1.1rem' }}>
                                    <i className="fas fa-info-circle me-2"></i>
                                    {t("home.no_product_offers")}
                                </div>
                            </div>
                        )}
                    </div>

                    {offersPagination.total > 1 && (
                        <motion.div
                            className="text-center mt-5"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.2 }}
                        >
                            {Array.from({ length: offersPagination.last_page }, (_, i) => (
                                <button
                                    key={i}
                                    onClick={() => handlePageChange(i + 1)}
                                    style={{
                                        backgroundColor: offersPagination.current_page === i + 1 
                                            ? (isDarkMode ? 'var(--color-primary)' : "#000c3d")
                                            : (isDarkMode ? 'var(--color-card)' : "transparent"),
                                        color: offersPagination.current_page === i + 1 
                                            ? "#fff" 
                                            : (isDarkMode ? 'var(--color-text)' : "#000c3d"),
                                        border: isDarkMode ? "1px solid var(--color-primary)" : "1px solid #000c3d",
                                        borderRadius: "10px",
                                        padding: "5px 12px",
                                        margin: "0 5px",
                                        cursor: "pointer"
                                    }}
                                    className={`btn mx-1 ${offersPagination.current_page === i + 1
                                        ? ""
                                        : "btn-outline-primary"
                                        }`}
                                >
                                    {i + 1}
                                </button>
                            ))}
                        </motion.div>
                    )}
                </div>
            </div>

            {/* Custom CSS for Offers */}
            <style jsx="true">{`
                
                .offers-section .swiper-pagination-bullet-custom {
                    width: 8px !important;
                    height: 8px !important;
                    background: rgba(255,255,255,0.5) !important;
                    opacity: 1 !important;
                    margin: 0 4px !important;
                    transition: all 0.3s ease !important;
                }
                
                .offers-section .swiper-pagination-bullet-active-custom {
                    background: #000c3d !important;
                    transform: scale(1.2) !important;
                }
                
                @keyframes pulse {
                    0% { transform: scale(1); }
                    50% { transform: scale(1.05); }
                    100% { transform: scale(1); }
                }
                
                /* Responsive adjustments for offers */
                @media (max-width: 768px) {
                    .offers-section .mythic-product-card {
                        margin-bottom: 20px;
                    }
                }
                
                @media (max-width: 576px) {
                    .offers-section .product-body-content {
                        padding: 10px !important;
                    }
                    
                    .offers-section .product-media-wrap {
                        height: 200px !important;
                    }
                }
            `}</style>
        </motion.section>
    );
}




function BestMarka() {
    const { t, i18n } = useTranslation("global");
    const { isDarkMode } = useDarkMode();
    const dispatch = useDispatch();
    
    const { data: productionCompanies = [], isLoading, error } = useSelector(state => state.productionCompany);

    const memoizedCompanies = useMemo(() => productionCompanies, [productionCompanies]);

    useEffect(() => {
        dispatch(fetchProductionCompany(i18n.language));
    }, [dispatch, i18n.language]);

    const cardVariants = {
        hidden: { opacity: 0, scale: 0.9, y: 20 },
        visible: (i) => ({
            opacity: 1,
            scale: 1,
            y: 0,
            transition: {
                delay: i * 0.1,
                duration: 0.5,
                ease: "easeOut"
            }
        })
    };

    return (
        <section className={`production-companies-section py-5 ${isDarkMode ? 'dark-mode' : ''}`} style={{ 
            background: isDarkMode ? '#03081a' : 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            position: 'relative',
            overflow: 'hidden'
        }}>
            {/* Background Decorative Elements */}
            <div className="mythic-bg-blob top-left"></div>
            <div className="mythic-bg-blob bottom-right"></div>

            <div className="container position-relative" style={{ zIndex: 2 }}>
                {/* Section Header */}
                <div className="text-center mb-5">
                    <motion.h3 
                        initial={{ opacity: 0, y: -20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="fw-bold mb-3 section-title-mythic"
                        style={{ color: isDarkMode ? 'var(--color-text)' : '#000c3d', fontSize: '2.8rem' }}
                    >
                        {t('home.brands.title')}
                    </motion.h3>
                    <motion.p 
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2 }}
                        className="text-muted section-subtitle-mythic" 
                        style={{ fontSize: '1.2rem', maxWidth: '700px', margin: '0 auto' }}
                    >
                        {t('home.brands.subtitle')}
                    </motion.p>
                </div>

                {isLoading ? (
                    <div className="marquee-container-mythic" style={{ minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div className="text-center">
                            <div className="mythic-spinner"></div>
                            <p className="mt-3 text-muted">{t('home.loading')}</p>
                        </div>
                    </div>
                                ) : error ? (
                    <div className="text-center my-4">
                        <div className="alert alert-danger mythic-alert" role="alert">
                            {error}
                        </div>
                    </div>
                ) : (
                    <div className="brands-swiper-wrapper-mythic">
                        <Swiper
                            key={`brands-swiper-${i18n.language}-${isDarkMode}`}
                            dir="ltr"
                            modules={[Autoplay, FreeMode]}
                            spaceBetween={50}
                            slidesPerView={'auto'}
                            loop={true}
                            speed={3000}
                            autoplay={{
                                delay: 0,
                                disableOnInteraction: false,
                                reverseDirection: i18n.language === 'ar',
                            }}
                            freeMode={{
                                enabled: true,
                                momentum: false,
                            }}
                            allowTouchMove={true}
                            className="brands-swiper-mythic"
                        >
                            {memoizedCompanies && memoizedCompanies.map((company, index) => (
                                <SwiperSlide key={company.id} style={{ width: 'auto' }}>
                                    <div className="mythic-brand-card-outer">
                                        <motion.div
                                            className={`mythic-brand-card ${isDarkMode ? 'dark-mode' : ''}`}
                                            variants={cardVariants}
                                            custom={index}
                                            initial="hidden"
                                            whileInView="visible"
                                            viewport={{ once: true }}
                                            whileHover={{ 
                                                y: -25,
                                                scale: 1.05,
                                                boxShadow: isDarkMode 
                                                    ? '0 30px 60px rgba(255, 0, 0, 0.2), 0 0 40px rgba(255, 0, 0, 0.1)' 
                                                    : '0 30px 60px rgba(0, 12, 61, 0.15)'
                                            }}
                                        >
                                            <div className="brand-card-glass-shine"></div>
                                            <div className="brand-card-glow"></div>
                                            
                                            {/* Icon Section */}
                                            <div className="brand-logo-wrapper">
                                                <div className="brand-inner-orb">
                                                    {company.image ? (
                                                        <img src={company.image} alt={company.name} className="brand-logo-img" />
                                                    ) : (
                                                        <i className="fas fa-industry brand-default-icon"></i>
                                                    )}
                                                </div>
                                                <div className="brand-aura-rings">
                                                    <div className="ring"></div>
                                                    <div className="ring"></div>
                                                    <div className="ring"></div>
                                                </div>
                                            </div>

                                            {/* Content Section */}
                                            <div className="brand-info-content">
                                                <h4 className="brand-name-mythic">{company.name}</h4>
                                                <div className="brand-status-badge">
                                                    <div className="status-dot"></div>
                                                    <span>{t('home.brands.premium')}</span>
                                                </div>
                                                <div className="brand-divider-mythic"></div>
                                                <p className="brand-desc-mythic">
                                                    {company.description || t('home.brands.viewCompany')}
                                                </p>
                                            </div>

                                            {/* Bottom Decoration */}
                                            <div className="card-decoration-bottom"></div>
                                        </motion.div>
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>
                )}
                {/* Companies Counter */}
                {memoizedCompanies && memoizedCompanies.length > 0 && (
                    <div className="text-center mt-5">
                        <motion.div 
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            className="brands-counter-pill"
                        >
                            <span className="count">{memoizedCompanies.length}</span>
                            <span className="label text-muted">{t('home.brands.companies')}</span>
                        </motion.div>
                    </div>
                )}
            </div>

            <style jsx="true">{`
                .production-companies-section {
                    position: relative;
                    padding: 60px 0 100px !important;
                }

                .brands-swiper-wrapper-mythic {
                    padding: 30px 0;
                    overflow: visible;
                }

                .brands-swiper-mythic {
                    overflow: visible !important;
                }

                .brands-swiper-mythic .swiper-wrapper {
                    transition-timing-function: linear !important;
                }

                .mythic-brand-card-outer {
                    padding: 20px;
                }

                .mythic-brand-card {
                    width: 320px;
                    height: 240px;
                    background: ${isDarkMode ? '#111827' : '#ffffff'};
                    border-radius: 20px;
                    padding: 30px;
                    position: relative;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.4s ease;
                    border: 1px solid ${isDarkMode ? '#1f2937' : '#e5e7eb'};
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
                    overflow: hidden;
                    cursor: pointer;
                }

                .mythic-brand-card:hover {
                    transform: translateY(-10px);
                    border-color: var(--color-primary);
                    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
                }

                .brand-card-glass-shine, .brand-card-glow, .brand-aura-rings, .card-decoration-bottom {
                    display: none;
                }

                .brand-logo-wrapper {
                    width: 100px;
                    height: 100px;
                    margin-bottom: 25px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    position: relative;
                }

                .brand-inner-orb {
                    width: 100%;
                    height: 100%;
                    background: ${isDarkMode ? '#1a1f2e' : '#ffffff'};
                    border-radius: 24px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
                    border: 1px solid ${isDarkMode ? '#2d3748' : '#edf2f7'};
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
                    padding: 15px;
                }

                .mythic-brand-card:hover .brand-inner-orb {
                    background: var(--color-primary);
                    border-color: var(--color-primary);
                    transform: translateY(-5px) scale(1.05);
                    box-shadow: 0 10px 20px rgba(var(--color-primary-rgb), 0.3);
                }

                .brand-logo-img {
                    width: 100%;
                    height: 100%;
                    object-fit: contain;
                    transition: all 0.5s ease;
                    filter: ${isDarkMode ? 'brightness(0) invert(1)' : 'grayscale(100%) opacity(0.8)'};
                }

                .mythic-brand-card:hover .brand-logo-img {
                    filter: brightness(0) invert(1) grayscale(0%) opacity(1);
                    transform: scale(1.1);
                }

                .brand-default-icon {
                    font-size: 2rem;
                    color: ${isDarkMode ? '#4a5568' : '#cbd5e0'};
                    transition: all 0.3s ease;
                }

                .mythic-brand-card:hover .brand-default-icon {
                    color: #ffffff;
                }

                .brand-info-content {
                    text-align: center;
                }

                .brand-name-mythic {
                    font-weight: 700;
                    font-size: 1.3rem !important;
                    color: ${isDarkMode ? '#f9fafb' : '#111827'};
                    margin-bottom: 6px;
                }

                .brand-status-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    margin-bottom: 10px;
                }

                .status-dot {
                    width: 8px;
                    height: 8px;
                    background: #10b981;
                    border-radius: 50%;
                }

                .brand-status-badge span {
                    font-size: 0.75rem;
                    font-weight: 600;
                    color: #6b7280;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .brand-divider-mythic {
                    height: 2px;
                    width: 30px;
                    background: var(--color-primary);
                    margin: 0 auto 12px;
                    border-radius: 1px;
                }

                .brand-desc-mythic {
                    font-size: 0.875rem;
                    color: #9ca3af;
                    line-height: 1.4;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }

                .brands-counter-pill {
                    background: ${isDarkMode ? '#1f2937' : '#f3f4f6'};
                    padding: 10px 24px;
                    border-radius: 50px;
                    display: inline-flex;
                    align-items: center;
                    gap: 12px;
                }

                .brands-counter-pill .count {
                    font-size: 1.25rem;
                    font-weight: 700;
                    color: var(--color-primary);
                }

                .brands-counter-pill .label {
                    font-size: 0.875rem;
                    font-weight: 600;
                    color: #6b7280;
                }

                @media (max-width: 768px) {
                    .mythic-brand-card {
                        width: 280px;
                        height: 220px;
                    }
                }
            `}</style>
        </section>
    );
}

function HomeCategory() {
    const { t, i18n } = useTranslation("global");
    const { isDarkMode } = useDarkMode();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    // Memoized navigation handler
    const handleProductClick = useCallback((categoryId) => {
        navigate(`/categories/${categoryId}`);
    }, [navigate]);
    
    const { categories, isLoading, error } = useSelector(state => state.categories);
    const [visibleCategories, setVisibleCategories] = useState(8);

    useEffect(() => {
        dispatch(fetchCategories(i18n.language));
    }, [dispatch, i18n.language]);

    const handleLoadMore = useCallback(() => {
        setVisibleCategories(prev => prev + 4);
    }, []);

    const displayedCategories = categories ? categories.slice(0, visibleCategories) : [];
    const hasMoreCategories = categories && categories.length > visibleCategories;

    return (
        <section className={`categories-section py-5 ${isDarkMode ? 'dark-mode' : ''}`} style={{ 
            background: isDarkMode ? '#0a0e27' : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
            minHeight: '400px'
        }}>
            <div className="container">
                {/* Section Header */}
                <div className="text-center mb-5">
                    <h2 className="fw-bold mb-3" style={{ color: isDarkMode ? 'var(--color-text)' : '#000c3d', fontSize: '2.5rem' }}>
                        {t('home.categories.title')}
                    </h2>
                    <p className="text-muted" style={{ fontSize: '1.2rem', color: isDarkMode ? 'var(--color-muted)' : undefined }}>
                        {t('home.categories.subtitle')}
                    </p>
                </div>

                {isLoading ? (
                    <div className="text-center my-5">
                        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">{t('home.loading')}</p>
                    </div>
                ) : error ? (
                    <div className="text-center my-5">
                        <div className="alert alert-danger" role="alert">
                            {error}
                        </div>
                    </div>
                ) : (
                    <div className="row g-4 justify-content-center">
                        {displayedCategories.map((category, index) => (
                            <div className="col-12 col-sm-6 col-md-4 col-lg-3" key={category.id}>
                                <motion.div
                                    className={`premium-category-card ${isDarkMode ? 'dark-mode' : ''}`}
                                    variants={cardVariants}
                                    custom={index}
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true }}
                                    onClick={() => handleProductClick(category.id)}
                                >
                                    <div className="category-image-wrapper">
                                        <img 
                                            src={category.image || '/Frame.png'} 
                                            alt={category.name} 
                                            className="category-bg-image"
                                            onError={(e) => { e.target.src = "/Frame.png"; }}
                                        />
                                        <div className="category-glass-overlay">
                                            <div className="category-content-top">
                                                <h3 className="category-main-title">{category.name}</h3>
                                            </div>
                                            <div className="category-content-bottom">
                                                <button className="category-explore-btn">
                                                    <i className={`fas ${i18n.language === 'ar' ? 'fa-arrow-right' : 'fa-arrow-left'} me-2`}></i>
                                                    {t('home.categories.explore')}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="category-hover-glow"></div>
                                </motion.div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Load More Categories Button */}
                {hasMoreCategories && (
                    <div className="text-center mt-5">
                        <button 
                            className="btn btn-primary btn-lg px-5 py-3 rounded-pill"
                            onClick={handleLoadMore}
                            style={{
                                background: 'linear-gradient(135deg, var(--color-primary) 0%, #ff4d4d 100%)',
                                border: 'none',
                                fontSize: '1.1rem',
                                fontWeight: '700',
                                boxShadow: '0 8px 30px rgba(255, 0, 0, 0.2)',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                color: 'white'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.transform = 'translateY(-3px) scale(1.02)';
                                e.target.style.boxShadow = '0 15px 40px rgba(255, 0, 0, 0.3)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.transform = 'translateY(0) scale(1)';
                                e.target.style.boxShadow = '0 8px 30px rgba(255, 0, 0, 0.2)';
                            }}>
                            {t('home.categories.loadMore')}
                        </button>
                    </div>
                )}

                {/* Categories Counter */}
                {categories && categories.length > 0 && (
                    <div className="text-center mt-4">
                        <p className="text-muted" style={{ fontSize: '0.9rem' }}>
                            {t('home.categories.showing')} {displayedCategories.length} {t('home.categories.of')} {categories.length} {t('home.categories.categories')}
                        </p>
                    </div>
                )}
            </div>

        </section>
    );
}


function AllProducts() {
    const { t, i18n } = useTranslation("global");
    const { isDarkMode } = useDarkMode();
    const isRTL = i18n.language === "ar";

    const tabs = [
        t("home.tabs.all")
    ];
    const [activeTab, setActiveTab] = useState(t("home.tabs.all"));

    useEffect(() => {
        setActiveTab(t("home.tabs.all"));
    }, [i18n.language]);
    const [currentPage, setCurrentPage] = useState(1);
    const [productQuantities, setProductQuantities] = useState({});

    const handleUpdateQuantity = (product, delta) => {
        const step = product.count || 1;
        setProductQuantities(prev => {
            const currentQty = prev[product.id] || step;
            const newQty = Math.max(step, currentQty + (delta * step));
            return { ...prev, [product.id]: newQty };
        });
    };

    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    // Memoized navigation handler
    const handleProductClick = useCallback((productId) => {
        navigate(`/productDetails/${productId}`);
    }, [navigate]);

    const {
        products = [],
        pagination,
        isLoading,
        error,
    } = useSelector((state) => state.products);

    const {
        newest = [],
        pagination: newestPagination = {},
        isLoading: newestLoading,
    } = useSelector((state) => state.newest);
    // //console.log(newest);


    const { items: favoriteItems = [] } = useSelector((state) => state.favorite) || {};
    const userTools = JSON.parse(localStorage.getItem("userTools"));
    const token = localStorage.getItem("token") || userTools?.token;
    const isSocial = token === 'social_login_placeholder';

    useEffect(() => {
        dispatch(fetchAllProducts({ lang: i18n.language, page: currentPage }));
        if (token && !isSocial) {
            dispatch(fetchFavorites(i18n.language));
        }

        if (activeTab === t("home.tabs.new")) {
            dispatch(fetchProductNewest({ lang: i18n.language, page: currentPage }));
        }
    }, [dispatch, currentPage, activeTab, token, isSocial]);

    const handleToggleFavorite = useCallback((e, productId) => {
        e.stopPropagation();
        if (!token || isSocial) {
            toast.info(t('home.loginRequired'));
            return;
        }
        dispatch(toggleFavorite({ productId, language: i18n.language }))
            .unwrap()
            .then((res) => {
                toast.success(res.message || t('favorites.updated'));
                // إعادة جلب المفضلة بعد التحديث مباشرة
                setTimeout(() => {
                    dispatch(fetchFavorites(i18n.language));
                }, 100);
            })
            .catch((err) => toast(err || t('favorites.error')));
    }, [dispatch, token, i18n.language, t]);

    const isFavorite = useCallback((productId) => {
        return favoriteItems?.some(item => item.id === productId);
    }, [favoriteItems]);

    const filteredProducts = useMemo(() => {
        if (activeTab === t("home.tabs.all")) {
            return products;
        } else if (activeTab === t("home.tabs.new")) {
            return newest;
        } else if (activeTab === t("home.tabs.best")) {
            return [...products].sort(
                (a, b) => parseFloat(b.final_price) - parseFloat(a.final_price)
            );
        }
        return products;
    }, [activeTab, products, newest]);


    const currentPagination =
        activeTab === t("home.tabs.new") ? newestPagination : pagination;

    const loading = activeTab === t("home.tabs.new") ? newestLoading : isLoading;


    return (
        <section className={`all-products py-5 ${isDarkMode ? 'dark-mode' : ''}`} style={{ background: isDarkMode ? '#0a0e27' : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
            <div className="container">
                <div className="row">
                    {/* Section Header */}
                    <div className="col-12 text-center mb-5">
                        <h2 className="fw-bold mb-3" style={{ color: isDarkMode ? 'var(--color-text)' : '#000c3d', fontSize: '2.5rem' }}>
                            {t('home.allProducts.title')}
                        </h2>
                        <p className="text-muted" style={{ fontSize: '1.2rem', color: isDarkMode ? 'var(--color-muted)' : undefined }}>
                            {t('home.allProducts.subtitle')}
                        </p>
                    </div>

                    {/* Tabs */}
                    <div className="col-12 mb-4">
                        <div className="d-flex justify-content-center gap-3 flex-wrap">
                            {tabs.map((tab, i) => (
                                <button
                                    key={i}
                                    className={`btn px-4 py-2 rounded-pill fw-semibold transition-all ${
                                        tab === activeTab 
                                            ? "btn-primary" 
                                            : "btn-outline-primary"
                                    }`}
                                    onClick={() => {
                                        setActiveTab(tab);
                                        setCurrentPage(1);
                                    }}
                                    style={{
                                        background: tab === activeTab ? '#000c3d' : 'transparent',
                                        color: tab === activeTab ? 'white' : '#000c3d',
                                        border: '2px solid #000c3d',
                                        fontSize: '1rem',
                                        transition: 'all 0.3s ease',
                                        transform: tab === activeTab ? 'translateY(-2px)' : 'none',
                                        boxShadow: tab === activeTab ? '0 8px 25px rgba(0,12,61,0.3)' : 'none'
                                    }}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Products */}
                    <div className="col-12">

                        {loading ? (
                            <div className="text-center my-5">
                                <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3 text-muted">{t('home.loading')}</p>
                            </div>
                        ) : Array.isArray(filteredProducts) && filteredProducts?.length > 0 ? (
                            <div className="row g-4">
                                {Array.isArray(filteredProducts) && filteredProducts.map((product, index) => (
                                    <div className="col-12 col-sm-6 col-lg-3" key={index}>
                                        <motion.div
                                            className={`mythic-product-card ${isDarkMode ? 'dark-mode' : ''}`}
                                            variants={cardVariants}
                                            custom={index}
                                            initial="hidden"
                                            whileInView="visible"
                                            viewport={{ once: true }}
                                            onClick={() => handleProductClick(product.id)}
                                        >
                                            {/* Header Actions */}
                                            <div className="card-actions-top">
                                                <button 
                                                    className={`action-btn fav-btn ${isFavorite(product.id) ? 'active' : ''}`}
                                                    onClick={(e) => handleToggleFavorite(e, product.id)}
                                                >
                                                    <FaHeart />
                                                </button>
                                                {product.offer_percent > 0 && (
                                                    <span className="discount-badge">-{product.offer_percent}%</span>
                                                )}
                                            </div>

                                            {/* Product Media */}
                                            <div className="product-media-wrap">
                                                <img
                                                    src={product.images?.[0] || "/Frame.png"}
                                                    alt={product.name}
                                                    className="product-main-img"
                                                    onError={(e) => { e.target.src = "/Frame.png"; }}
                                                />
                                                <div className="media-overlay">
                                                    <span className="quick-view-tag">{t('home.allProducts.viewDetails')}</span>
                                                </div>
                                            </div>

                                            {/* Product Body */}
                                            <div className="product-body-content">
                                                <div className="category-tag">{product.category}</div>
                                                <h4 className="product-title-text">{product.name}</h4>
                                                {product.code && (
                                                    <div className="product-code small text-muted mb-1" style={{ fontSize: '0.85rem' }}>
                                                        {t('product.code')}: {product.code}
                                                    </div>
                                                )}
                                                
                                                <div className="price-section-new">
                                                    {product.final_price < product.price ? (
                                                        <>
                                                            <span className="current-price">{product.final_price} <small>{t("cart.currency")}</small></span>
                                                            <span className="old-price">{product.price} <small>{t("cart.currency")}</small></span>
                                                        </>
                                                    ) : (
                                                        <span className="current-price">{product.price} <small>{t("cart.currency")}</small></span>
                                                    )}
                                                </div>

                                                {/* Add to Cart Control */}
                                                <div className="cart-control-wrap" onClick={(e) => e.stopPropagation()}>
                                                    <div className="qty-selector">
                                                        <button onClick={() => handleUpdateQuantity(product, -1)}>−</button>
                                                        <span>{productQuantities[product.id] || product.count || 1}</span>
                                                        <button onClick={() => handleUpdateQuantity(product, 1)}>+</button>
                                                    </div>
                                                    <button 
                                                        className="mythic-add-btn"
                                                        onClick={() => {
                                                            const qty = productQuantities[product.id] || product.count || 1;
                                                            dispatch(addToCart({ 
                                                                product_id: product.id, 
                                                                qty: qty, 
                                                                language: i18n.language 
                                                            })).unwrap()
                                                            .then(() => {
                                                                toast.success(t('cart.addedSuccessfully'));
                                                                dispatch(fetchCartItems(i18n.language));
                                                            })
                                                            .catch((err) => toast(err.message || t('cart.addError')));
                                                        }}
                                                    >
                                                        <i className="fas fa-shopping-basket"></i>
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="card-hover-aura"></div>
                                        </motion.div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center my-5">{t('home.noProducts')}</div>
                        )}

                        {/* Collection Style Pagination */}
                        {currentPagination && currentPagination.total > 1 && (
                            <motion.div
                                className="text-center mt-5"
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.7, delay: 0.2 }}
                            >
                                {Array.from({ length: currentPagination.last_page }, (_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setCurrentPage(i + 1)}
                                        style={{
                                            backgroundColor: currentPagination.current_page === i + 1 
                                                ? (isDarkMode ? 'var(--color-primary)' : "#000c3d")
                                                : (isDarkMode ? 'var(--color-card)' : "transparent"),
                                            color: currentPagination.current_page === i + 1 
                                                ? "#fff" 
                                                : (isDarkMode ? 'var(--color-text)' : "#000c3d"),
                                            border: isDarkMode ? "1px solid var(--color-primary)" : "1px solid #000c3d",
                                            borderRadius: "10px",
                                            padding: "5px 12px",
                                            margin: "0 5px",
                                            cursor: "pointer"
                                        }}
                                        className={`btn mx-1 ${currentPagination.current_page === i + 1
                                            ? ""
                                            : "btn-outline-primary"
                                            }`}
                                    >
                                        {i + 1}
                                    </button>
                                ))}
                            </motion.div>
                        )}

                        {/* Results Info */}
                        {currentPagination && (
                            <div className="text-center mt-4">
                                <p className="text-muted" style={{ fontSize: '0.9rem' }}>
                                    {t('home.showingResults', { 
                                        from: ((currentPage - 1) * currentPagination.per_page) + 1,
                                        to: Math.min(currentPage * currentPagination.per_page, currentPagination.total),
                                        total: currentPagination.total
                                    })}
                                </p>
                            </div>
                        )}


                    </div>
                </div>
            </div>
        </section>
    );
}


export default Home;
